import { useContext, useEffect, useRef, useState } from 'react'
import { WppActionButton, WppIconExpand, WppTypography } from '@wppopen/components-library-react'
import HighlightedPopoverSpan from '../Highlighting/HighlightedPopoverSpan/HighlightedPopoverSpan'
import clsx from 'clsx'
import { KeywordsData } from 'reducers/KeywordsAndPhrasesReducer/KeywordsAndPhrasesReducer.d'
import styles from './ExpandablePopover.module.scss'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import {
  setActiveKeyword,
  setClickedKeyword,
  setHoveredKeyword,
} from 'reducers/KeywordsAndPhrasesReducer/KeywordsAndPhrasesReducer'
import Tabs from 'components/Tabs/Tabs'

interface ExpandablePopoverProps {
  isPopoverActive: boolean
  data: KeywordsData
}

export default function ExpandablePopover({ isPopoverActive, data }: ExpandablePopoverProps) {
  const dataKeys = Object.keys(data)
  const [currentTab, setCurrentTab] = useState(dataKeys[0].toLowerCase())
  const [isPopoverExpanded, setIsPopoverExpanded] = useState(false)
  const popover = useRef<HTMLDivElement | null>(null)
  const spacer = useRef<HTMLDivElement | null>(null)
  const { keywordsAndPhrasesReducerstate, keywordsAndPhrasesReducerDispatch } = useContext(ViewTaskPageContext)

  const handleExpand = () => {
    setIsPopoverExpanded(prev => !prev)
  }

  // local keyword click
  const handleKeywordClick = (keyword: string) => {
    if (keyword === keywordsAndPhrasesReducerstate.clickedKeyword) {
      return keywordsAndPhrasesReducerDispatch(setClickedKeyword(null))
    }
    keywordsAndPhrasesReducerDispatch(setClickedKeyword(keyword))
  }

  const handleKeywordHover = (keyword: string) => {
    keywordsAndPhrasesReducerDispatch(setHoveredKeyword(keyword))
  }

  const handleKeywordBlur = () => {
    keywordsAndPhrasesReducerDispatch(setHoveredKeyword(null))
  }

  // update setActiveKeyword in PDP
  useEffect(() => {
    if (!isPopoverActive) {
      return keywordsAndPhrasesReducerDispatch(setActiveKeyword(null))
    }
    keywordsAndPhrasesReducerDispatch(
      setActiveKeyword(keywordsAndPhrasesReducerstate.clickedKeyword || keywordsAndPhrasesReducerstate.hoveredKeyword),
    )
  }, [keywordsAndPhrasesReducerstate.clickedKeyword, keywordsAndPhrasesReducerstate.hoveredKeyword, isPopoverActive])

  // add popover height to spacer margin
  useEffect(() => {
    // let timeoutId: Timeout | undefined
    if (!spacer.current || !popover.current || !isPopoverActive) {
      return
    }
    let timeoutId = setTimeout(
      () => {
        spacer.current!.style.marginBottom = `calc(${popover.current?.clientHeight}px + 40px)`
      },
      // if the spacer already has a margin bottom means the spacer just needs to be updated and, wait 300ms for css animation to finish
      spacer.current.style.marginBottom ? 300 : 50,
    )

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isPopoverActive, isPopoverExpanded])

  // if popover is not active, return null
  if (!isPopoverActive) {
    return null
  }

  return (
    <>
      <div ref={spacer} data-testid="spacer" />
      <div className={styles.popover} ref={popover} data-testid="keyword-popover-container">
        <div className={styles.popover_header} data-testid="keyword-popover-header">
          <WppActionButton aria-label="expand" onClick={handleExpand} data-testid="expand-button">
            <WppIconExpand />
          </WppActionButton>
        </div>

        <Tabs currentTab={currentTab} dataKeys={dataKeys} onSetNewTab={setCurrentTab} className={styles.popover_nav} />

        <div
          className={clsx(styles.popover_container, { [styles.expand]: isPopoverExpanded })}
          data-testid="keyword-popover-content"
        >
          {data?.[currentTab].map(({ title, type, keys }) => (
            <div key={title} className={styles.key_container} data-testid="single-keyword-data-section">
              <WppTypography tag="h4" type="m-strong" className={styles.title}>
                {title}{' '}
                <span>
                  ({keys.filter(({ currentCount }) => currentCount !== 0).length}/{keys.length})
                </span>
              </WppTypography>

              {keys.map(({ key: keyword, currentCount, originalCount }) => {
                const isInactive =
                  (!!keywordsAndPhrasesReducerstate.clickedKeyword &&
                    keywordsAndPhrasesReducerstate.clickedKeyword !== keyword) ||
                  (!!keywordsAndPhrasesReducerstate.hoveredKeyword &&
                    keywordsAndPhrasesReducerstate.clickedKeyword !== keyword &&
                    keywordsAndPhrasesReducerstate.hoveredKeyword !== keyword)
                return (
                  <HighlightedPopoverSpan
                    type={type}
                    currentNumberOfKeywords={currentCount}
                    key={`${keyword}-${currentCount}-${originalCount}`}
                    isInactive={isInactive}
                    onHover={() => handleKeywordHover(keyword)}
                    onLeave={handleKeywordBlur}
                    onClick={() => handleKeywordClick(keyword)}
                  >
                    {keyword} <b>{currentCount}</b> {!!originalCount && <span>({originalCount})</span>}
                  </HighlightedPopoverSpan>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
